import { inject } from '@angular/core';
import { ActionReducer, INIT, UPDATE } from '@ngrx/store';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { Crypto } from '../../services/helpers/encryption-utils';
import { RootReducerState } from '../reducers';
import {
  LEGACY_PERSIST_STORAGE_KEY,
  PERSIST_STORAGE_KEY,
  PersistedRootState,
  mergePersistedIntoState,
  pickPersistedState,
} from '../utils/persisted-state';
import { buildStateSizeReport, bytesToMb, logStateStorageSizes, measureJsonPayload } from '../utils/state-size';

const PERSIST_DEBOUNCE_MS = 400;
let persistHandle: ReturnType<typeof setTimeout> | null = null;
let lastSerialized = '';

function readPersisted(localStorageService: LocalStorageService): PersistedRootState | null {
  const raw = localStorageService.GetItem(PERSIST_STORAGE_KEY) ?? localStorageService.GetItem(LEGACY_PERSIST_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const decrypted = Crypto.decrypt(raw);
    if (decrypted?.config) {
      const persisted = decrypted as PersistedRootState;
      logStateStorageSizes('hydrate', buildStateSizeReport(persisted, raw), { storageKey: PERSIST_STORAGE_KEY });
      return persisted;
    }
    // Legacy blob stored the entire RootReducerState — only reuse safe config fields.
    if (decrypted?.config?.pdf_generator || decrypted?.config?.menu) {
      return pickPersistedState(decrypted as RootReducerState);
    }
    return null;
  } catch {
    localStorageService.RemoveItem(PERSIST_STORAGE_KEY);
    localStorageService.RemoveItem(LEGACY_PERSIST_STORAGE_KEY);
    return null;
  }
}

function schedulePersist(localStorageService: LocalStorageService, state: RootReducerState): void {
  const snapshot = pickPersistedState(state);
  const serialized = JSON.stringify(snapshot);
  if (serialized === lastSerialized) {
    return;
  }
  if (persistHandle) {
    clearTimeout(persistHandle);
  }
  persistHandle = setTimeout(() => {
    lastSerialized = serialized;
    const encrypted = Crypto.encrypt(snapshot);
    localStorageService.SetItem(PERSIST_STORAGE_KEY, encrypted);
    localStorageService.RemoveItem(LEGACY_PERSIST_STORAGE_KEY);
    logStateStorageSizes('persist', buildStateSizeReport(snapshot, encrypted), {
      storageKey: PERSIST_STORAGE_KEY,
      inMemoryMb: bytesToMb(measureJsonPayload(state).jsonBytes),
    });
    persistHandle = null;
  }, PERSIST_DEBOUNCE_MS);
}

export function clearPersistedStore(localStorageService: LocalStorageService): void {
  if (persistHandle) {
    clearTimeout(persistHandle);
    persistHandle = null;
  }
  lastSerialized = '';
  localStorageService.RemoveItem(PERSIST_STORAGE_KEY);
  localStorageService.RemoveItem(LEGACY_PERSIST_STORAGE_KEY);
}

export const hydrationMetaReducer = (reducer: ActionReducer<RootReducerState>): ActionReducer<RootReducerState> => {
  const localStorageService = inject(LocalStorageService);

  return (state, action) => {
    if (action.type === INIT || action.type === UPDATE) {
      const persisted = readPersisted(localStorageService);
      const base = reducer(state, action);
      return persisted ? mergePersistedIntoState(base, persisted) : base;
    }

    const nextState = reducer(state, action);
    schedulePersist(localStorageService, nextState);
    return nextState;
  };
};
